import { OktaAuth, TokenResponse } from "@okta/okta-auth-js";
import { appContext } from "../AppContext";

export default class Authentication
{
    private get localStorage() { return appContext().localStorageProvider; }
    private get sessionStorage() { return appContext().sessionStorageProvider; }
    private get cache() { return appContext().cache; }
    private refreshPromise: Promise<string> | null = null;
    private lastProactiveRefreshAttempt = 0;

    private readonly proactiveRefreshThresholdSeconds = 120;
    private readonly proactiveRefreshCooldownMs = 30_000;
    private readonly refreshTimeoutMs = 10_000;

    public getToken(): string
    {
        const token = this.localStorage.getToken();
        if (!token)
        {
            return "";
        }

        if (!this.isTokenValid(0, token))
        {
            this.localStorage.setToken("");
            return "";
        }

        if (this.isTokenExpiringSoon(this.proactiveRefreshThresholdSeconds, token))
        {
            this.tryProactiveRefresh();
        }

        return token;
    }

    public setToken(value: string): void
    {
        appContext().localStorageProvider.setToken(value);
    }

    public isTokenValid(clockSkewSeconds = 0, token = this.localStorage.getToken()): boolean
    {
        if (!token)
        {
            return false;
        }

        const expiresOn = this.getTokenExpiry(token);
        if (expiresOn === null)
        {
            // If this is not a JWT, retain existing behavior and treat it as valid.
            return true;
        }

        const now = Math.floor(Date.now() / 1000);
        return expiresOn > (now + clockSkewSeconds);
    }

    public isTokenExpiringSoon(thresholdSeconds = this.proactiveRefreshThresholdSeconds, token = this.localStorage.getToken()): boolean
    {
        if (!token)
        {
            return false;
        }

        const expiresOn = this.getTokenExpiry(token);
        if (expiresOn === null)
        {
            return false;
        }

        const now = Math.floor(Date.now() / 1000);
        return expiresOn <= (now + thresholdSeconds);
    }

    private getTokenExpiry(token: string): number | null
    {
        try
        {
            const jwtParts = token.split(".");
            if (jwtParts.length < 2)
            {
                return null;
            }

            const payload = jwtParts[1]
                .replace(/-/g, "+")
                .replace(/_/g, "/");

            const paddedPayload = payload + "=".repeat((4 - (payload.length % 4)) % 4);
            const decodedPayload = atob(paddedPayload);
            const parsedPayload = JSON.parse(decodedPayload) as { exp?: number };

            if (typeof parsedPayload.exp !== "number")
            {
                return null;
            }

            return parsedPayload.exp;
        }
        catch
        {
            return null;
        }
    }

    public async logOutIfTokenInvalid(): Promise<void>
    {
        const token = this.localStorage.getToken();
        if (!token)
        {
            return;
        }

        if (!this.isTokenValid())
        {
            await this.logOut(LogOutReason.SessionExpired);
            return;
        }

        if (this.isTokenExpiringSoon())
        {
            try
            {
                await this.refreshToken();
            }
            catch
            {
                await this.logOut(LogOutReason.SessionExpired);
            }
        }
    }

    public async refreshToken(timeoutMs = this.refreshTimeoutMs): Promise<string>
    {
        if (this.refreshPromise)
        {
            return this.refreshPromise;
        }

        this.refreshPromise = this
            .withTimeout(signal => this.runRefreshToken(signal), timeoutMs)
            .finally(() =>
            {
                this.refreshPromise = null;
            });

        return this.refreshPromise;
    }

    private withTimeout<T>(taskFactory: (signal: AbortSignal) => Promise<T>, timeoutMs: number): Promise<T>
    {
        const timeoutController = new AbortController();
        const timeoutId = window.setTimeout(() => timeoutController.abort(), timeoutMs);

        const timeoutTask = new Promise<T>((_, reject) =>
        {
            timeoutController.signal.addEventListener(
                "abort",
                () => reject(new Error("Token refresh timed out.")),
                { once: true }
            );
        });

        const task = taskFactory(timeoutController.signal);

        return Promise.race([task, timeoutTask]).finally(() =>
        {
            window.clearTimeout(timeoutId);
        });
    }

    private async runRefreshToken(signal: AbortSignal): Promise<string>
    {
        const securityProviderTypeId = this.localStorage.getUserDetails().providerType ?? '';
        let token = '';

        switch (securityProviderTypeId)
        {
            case 'Native':
                throw new Error('Native authentication does not support refreshing tokens.');
            case 'AAD':
                token = await this.refreshAadToken(signal);
                break;
            case 'Okta':
                token = await this.refreshOktaToken(signal);
                break;
            case 'Auth0':
                token = await this.refreshAuth0Token(signal);
                break;
            default:
                throw new Error('Not supported.');
        }

        if (signal.aborted)
        {
            throw new Error('Token refresh timed out.');
        }

        this.localStorage.setToken(token);
        return token;
    }

    private tryProactiveRefresh(): void
    {
        const now = Date.now();
        const canAttemptRefresh = (now - this.lastProactiveRefreshAttempt) > this.proactiveRefreshCooldownMs;
        if (!canAttemptRefresh)
        {
            return;
        }

        this.lastProactiveRefreshAttempt = now;
        this.refreshToken().catch(() => { return; });
    }

    private async refreshAadToken(signal: AbortSignal): Promise<string>
    {
        try
        {
            const tokenResponse = await appContext().msalProvider.getToken();
            if (signal.aborted)
            {
                throw new Error('Token refresh timed out.');
            }

            if (!tokenResponse.accessToken)
            {
                throw new Error('Failed to refresh AAD token.');
            }

            return tokenResponse.accessToken;
        }
        catch (tokenError)
        {
            throw new Error('Failed to refresh AAD token.');
        }
    }

    private async refreshOktaToken(signal: AbortSignal): Promise<string>
    {
        let tokenStorage = JSON.parse(localStorage.getItem("okta-token-storage") ?? "{}");
        const config =
        {
            issuer: tokenStorage.idToken.issuer,
            clientId: tokenStorage.idToken.clientId,
            redirectUri: window.location.origin + "/implicit/callback",
            scopes: ['openid', 'email', 'profile'],
        };

        let authClient = new OktaAuth(config);

        try
        {
            const tokenResponse: TokenResponse = await authClient.token.getWithoutPrompt();
            if (signal.aborted)
            {
                throw new Error('Token refresh timed out.');
            }

            if (!tokenResponse?.tokens?.idToken?.idToken)
            {
                throw new Error('Failed to refresh OKTA token.');
            }

            authClient.tokenManager.setTokens(tokenResponse.tokens);
            let idToken = tokenResponse.tokens.idToken.idToken;
            return idToken;
        }
        catch (error)
        {
            throw new Error('Failed to refresh OKTA token.');
        };
    }

    private async refreshAuth0Token(signal: AbortSignal): Promise<string>
    {
        try
        {
            const token = await appContext().auth0Provider.getToken();
            if (signal.aborted)
            {
                throw new Error('Token refresh timed out.');
            }

            if (!token)
            {
                throw new Error('Failed to refresh Auth0 token.');
            }

            return token;
        }
        catch (error)
        {
            throw new Error('Failed to refresh Auth0 token.');
        }
    }

    public async logOut(reason?: LogOutReason): Promise<void>
    {
        await this.clearStorage();
        window.location.href = `/login${reason ? '?reason=' + LogOutReason[reason] : ''}`;
    }

    public async clearStorage(): Promise<void>
    {
        const currentLanguage = appContext().language.current;
        this.sessionStorage.clear();
        this.localStorage.clear();
        this.cache.clearAll();
        await appContext().language.setCurrent(currentLanguage);
    }
}

export enum LogOutReason
{
    NotSet = 0,
    SessionExpired,
    AccountIncomplete,
    LoginFailed,
    VersionChanged,
}
